// import { useMapInject } from "../plotter/mapInjectContext"
import React, { useEffect, useState } from 'react';
import { ClockIcon } from '@iconicicons/react';
import { LoadingButton } from '../helpers/LoadingButton';
import { Collapse } from 'reactstrap';
import { CapacityComponentFlowsTable } from './CapacityComponentFlowsTable';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { FloatingLabelInput } from '../helpers/FloatingLabelInput';
import { PriceComponentPricesTable } from '../priceComponents/PriceComponentPricesTable';
import { toIsoDate } from '../utils/stringUtils';
// import { MapNotices } from './MapNotices';
import { ExtractedNoticeTable } from './ExtractedNoticeTable';
import { NoticeRecordTable } from './NoticeRecordTable';
import { useGetPriceComponentPricesQuery } from '../services/edgeApi';
import { useDispatch } from 'react-redux';
import { plotterActions } from '../plotter/plotterSlice';
import { BelowMapTab } from '../layouts/BelowMapTab';

export const MarketData = (props) => {
  const dispatch = useDispatch();
  const [gasDate, setGasDate] = useState(
    props.gasDate ?? toIsoDate(new Date()),
  );
  const [nomCycle, setNomCycle] = useState(false);
  const [priceSource, setPriceSource] = useState('PLATTS_DAILY');

  const { data: priceComponents, refetch: refetchPriceComponents } =
    useGetPriceComponentPricesQuery(
      { gasDate, priceSource },
      { pollingInterval: priceSource === 'ICE_LIVE' ? 10000 : 0 },
    );

  useEffect(() => {
    return () => {
      dispatch(plotterActions.setCapacities([]));
      dispatch(plotterActions.setMarkets([]));
    };
  }, []);

  useEffect(() => {
    refetchPriceComponents();
  }, [gasDate, priceSource]);

  return (
    <>
      <GasDatePicker
        setGasDate={setGasDate}
        setNomCycle={setNomCycle}
        isFetching={false}
      />
      <div className="form-floating my-3">
        <select
          className="form-select"
          value={priceSource}
          onChange={(evt) => setPriceSource(evt.target.value)}
        >
          <option value="PLATTS_DAILY">Platts Daily</option>
          <option value="ICE_DAILY">ICE Daily</option>
          <option value="ICE_LIVE">ICE Live</option>
          <option value="ICE_FUTURE">ICE Futures</option>
        </select>
        <label>Price Source</label>
      </div>
      <BelowMapSetter>
        <BelowMapTab tabName="Flows" contextKey="capacityComponents">
          <CapacityComponentFlowsTable nomCycle={nomCycle} gasDate={gasDate} />
        </BelowMapTab>
        <PriceComponentPricesTable
          tabName="Prices"
          contextKey="priceComponents"
          priceSource={priceSource}
          priceComponents={priceComponents}
          onFilterChange={(data) =>
            dispatch(
              plotterActions.setMarkets(
                Object.values(data).flatMap((component) =>
                  component.points.map((point) => ({
                    ...component,
                    point,
                    price: component.record_to_use?.values?.close,
                    price_source: priceSource,
                  })),
                ),
              ),
            )
          }
        />
        <NoticeRecordTable
          tabName="Notices"
          contextKey="parsed-notices"
          gasDate={gasDate}
        />
        <ExtractedNoticeTable
          tabName="Raw Notices"
          contextKey="raw-notices"
          gasDate={gasDate}
        />
      </BelowMapSetter>
    </>
  );
};

function GasDatePicker(props) {
  // gasDate, nomCycle, setGasDate, setNomCycle

  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const today = new Date();
  const tomorrow = new Date();
  tomorrow.setDate(today.getDate() + 1);
  const todayString = toIsoDate(today);
  const tomorrowString = toIsoDate(tomorrow);

  // these are internal state, submit sends them external and kicks off query
  const [gasDate, setGasDate] = useState(todayString);
  const [_nomCycle, setNomCycle] = useState('');

  // these are separate so they don't get changed when clicking "today gas date" so you can flip back and forth without reselecting the date
  const [customGasDate, setCustomGasDate] = useState(todayString);
  const [customNomCycle, setCustomNomCycle] = useState('');

  const handleSubmit = (evt) => {
    evt.preventDefault();
    props.setGasDate(customGasDate);
    props.setNomCycle(customNomCycle);
  };

  return (
    <div className="my-3">
      <div className="btn-group w-100">
        <LoadingButton
          isLoading={props.isFetching && gasDate === todayString}
          className={`btn btn-primary form-control ${
            gasDate === todayString && 'active'
          }`}
          onClick={() => {
            setGasDate(todayString);
            props.setGasDate(todayString);
            setCustomGasDate(todayString);
            setNomCycle('');
            props.setNomCycle('');
          }}
        >
          {"Today's Gas Day"}
        </LoadingButton>

        <LoadingButton
          isLoading={props.isFetching && gasDate === tomorrowString}
          className={`btn btn-primary form-control ${
            gasDate === tomorrowString && 'active'
          }`}
          onClick={() => {
            setGasDate(tomorrowString);
            props.setGasDate(tomorrowString);
            setCustomGasDate(tomorrowString);
            setNomCycle('');
            props.setNomCycle('');
          }}
        >
          {"Tomorrow's Gas Day"}
        </LoadingButton>
        <button
          className="btn btn-outline-primary"
          onClick={() => setShowCustomPicker(!showCustomPicker)}
          aria-label="Toggle custom date picker"
        >
          <ClockIcon />
        </button>
      </div>
      <Collapse isOpen={showCustomPicker}>
        <form id="custom-date" className="my-3" onSubmit={handleSubmit}>
          <FloatingLabelInput
            aria-label="Gas Date Selector"
            label="Gas Date"
            value={customGasDate}
            type="date"
            name="gasDate"
            onChange={(evt) => setCustomGasDate(evt.target.value)}
          />
          <FloatingLabelInput
            label="Nomination Cycle"
            value={customNomCycle}
            type="select"
            name="nomCycle"
            onChange={(evt) => setCustomNomCycle(evt.target.value)}
          >
            <option value="">Best Available</option>
            <option value="I3">I3</option>
            <option value="I2">I2</option>
            <option value="I1">I1</option>
            <option value="E">E</option>
            <option value="T">T</option>
          </FloatingLabelInput>
          <LoadingButton
            isLoading={props.isFetching}
            className="btn btn-primary w-100 form-control"
            type="submit"
          >
            {'Get Flows'}
          </LoadingButton>
        </form>
      </Collapse>
    </div>
  );
}
